/**
 * Маршруты §3.4 — все 19.
 *
 * Экраны наполняются на фазах 3–5. Здесь фиксируется структура: адреса,
 * вложенность, требуемые права.
 *
 * Заглушек не осталось: каждый маршрут ведёт на реальный экран (§28.4).
 */

import { AdminIndex, AdminLayout } from "@/features/admin/AdminLayout";
import { BASE_PATH } from "@/shared/api/basePath";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { InvitePage } from "@/features/auth/InvitePage";
import { ArchivePage } from "@/features/boards/ArchivePage";
import { ArchivedBoardsPage } from "@/features/boards/ArchivedBoardsPage";
import { BoardsPage } from "@/features/boards/BoardsPage";
import { DepartmentTasksPage } from "@/features/tasks/DepartmentTasksPage";
import { BoardSettingsPage } from "@/features/boards/settings/BoardSettingsPage";
import { BoardPage } from "@/features/boards/BoardPage";
import { TaskPanel } from "@/features/tasks/TaskPanel";
import { LoginPage } from "@/features/auth/LoginPage";
import { AppDownloadPage } from "@/features/mobileApp/AppDownloadPage";
import { ProtectedRoute } from "@/shared/auth/ProtectedRoute";

import { AppShell } from "./AppShell";
import { NotFound } from "./NotFound";
import {
  AttentionSection,
  Chunk,
  DepartmentsPage,
  DepartmentsSection,
  DictionaryPage,
  EmployeesSection,
  EntityDetailSection,
  NotesPage,
  NotificationsPage,
  OverviewSection,
  ProfilePage,
  ProjectsSection,
  ReportsLayout,
  ReportTasksSection,
  RolesPage,
  SubscriptionsPage,
  UserCardPage,
  UsersPage,
  VacationsLayout,
  VacationsOverviewPage,
  VacationsPlannerPage,
  VacationsProfilesPage,
  VacationsSettingsPage,
  WorkloadNormsPage,
} from "./lazyPages";

export const routeObjects = [
  // ── Гостевые маршруты: вне AppShell, своя композиция ────────────────
  { path: "/login", element: <LoginPage /> },
  { path: "/invite/:token", element: <InvitePage /> },
  // Установка Android-приложения: ссылка из письма-приглашения ведёт
  // сюда раньше, чем человек впервые вошёл.
  { path: "/app", element: <AppDownloadPage /> },

  // ── Приложение ──────────────────────────────────────────────────────
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/tasks" replace /> },

          // Задачник
          { path: "tasks", element: <BoardsPage /> },
          // Задачи всех досок подразделения списком.
          { path: "tasks/list", element: <DepartmentTasksPage /> },
          // Архив досок: статический путь выигрывает у boards/:boardId.
          { path: "boards/archived", element: <ArchivedBoardsPage /> },
          {
            path: "boards/:boardId",
            children: [
              {
                // Безпутевой layout: BoardPage рендерится и на самой доске,
                // и при открытой карточке. §3.4 требует, чтобы доска
                // оставалась смонтированной под панелью, а не заменялась ею.
                element: <BoardPage />,
                children: [
                  { index: true, element: null },
                  { path: "tasks/:taskKey", element: <TaskPanel /> },
                ],
              },
              { path: "archive", element: <ArchivePage /> },
              { path: "settings", element: <BoardSettingsPage /> },
            ],
          },

          // Отчётность
          {
            path: "reports",
            element: (
              <Chunk>
                <ReportsLayout />
              </Chunk>
            ),
            children: [
              // §25.7: карточка задачи открывается поверх того раздела, где
              // её нажали, и отчёт под ней не размонтируется. Прежде она была
              // только у «Задач»: из детализации сотрудника задача уводила
              // в общий список, из «Требует внимания» — на доску.
              { index: true, element: <OverviewSection /> },
              { path: "employees", element: <EmployeesSection /> },
              {
                path: "employees/:id",
                element: <EntityDetailSection kind="employees" />,
                children: [{ path: "tasks/:taskKey", element: <TaskPanel /> }],
              },
              { path: "departments", element: <DepartmentsSection /> },
              { path: "departments/:id", element: <EntityDetailSection kind="departments" /> },
              { path: "projects", element: <ProjectsSection /> },
              {
                path: "projects/:id",
                element: <EntityDetailSection kind="projects" />,
                children: [{ path: "tasks/:taskKey", element: <TaskPanel /> }],
              },
              {
                path: "tasks",
                element: <ReportTasksSection />,
                children: [{ path: ":taskKey", element: <TaskPanel /> }],
              },
              {
                path: "attention",
                element: <AttentionSection />,
                children: [{ path: "tasks/:taskKey", element: <TaskPanel /> }],
              },
            ],
          },

          // Заметки: список и открытая заметка — один экран, адрес у заметки свой.
          { path: "notes", element: (
              <Chunk>
                <NotesPage />
              </Chunk>
            ) },
          { path: "notes/:noteId", element: (
              <Chunk>
                <NotesPage />
              </Chunk>
            ) },

          // Отпуска: обзор, график, параметры сотрудников, правила и календарь.
          {
            path: "vacations",
            element: (
              <Chunk>
                <VacationsLayout />
              </Chunk>
            ),
            children: [
              { index: true, element: (
                  <Chunk>
                    <VacationsOverviewPage />
                  </Chunk>
                ) },
              { path: "planner", element: (
                  <Chunk>
                    <VacationsPlannerPage />
                  </Chunk>
                ) },
              { path: "employees", element: (
                  <Chunk>
                    <VacationsProfilesPage />
                  </Chunk>
                ) },
              { path: "settings", element: (
                  <Chunk>
                    <VacationsSettingsPage />
                  </Chunk>
                ) },
            ],
          },

          // Администрирование
          {
            path: "admin",
            element: <AdminLayout />,
            children: [
              { index: true, element: <AdminIndex /> },
              { path: "users", element: (
                  <Chunk>
                    <UsersPage />
                  </Chunk>
                ) },
              { path: "users/:id", element: (
                  <Chunk>
                    <UserCardPage />
                  </Chunk>
                ) },
              { path: "roles", element: (
                  <Chunk>
                    <RolesPage />
                  </Chunk>
                ) },
              { path: "dictionaries/projects", element: (
                  <Chunk>
                    <DictionaryPage key="projects" kind="projects" />
                  </Chunk>
                ) },
              { path: "dictionaries/priorities", element: (
                  <Chunk>
                    <DictionaryPage key="priorities" kind="priorities" />
                  </Chunk>
                ) },
              { path: "dictionaries/task-types", element: (
                  <Chunk>
                    <DictionaryPage key="task-types" kind="task-types" />
                  </Chunk>
                ) },
              { path: "dictionaries/workload", element: (
                  <Chunk>
                    <WorkloadNormsPage />
                  </Chunk>
                ) },
              { path: "dictionaries/departments", element: (
                  <Chunk>
                    <DepartmentsPage />
                  </Chunk>
                ) },
            ],
          },

          // Уведомления: история с фильтрами (§18.5)
          { path: "notifications", element: (
              <Chunk>
                <NotificationsPage />
              </Chunk>
            ) },

          // Профиль
          { path: "settings/profile", element: (
              <Chunk>
                <ProfilePage />
              </Chunk>
            ) },
          // Мои подписки: наблюдение за столбцами, досками, проектами, задачами.
          { path: "settings/subscriptions", element: (
              <Chunk>
                <SubscriptionsPage />
              </Chunk>
            ) },

          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
];

// basename — префикс приложения: все пути роутера и navigate() отсчитываются от него.
export const router = createBrowserRouter(routeObjects, { basename: BASE_PATH || "/" });

/** Плоский список путей — используется тестом полноты маршрутов. */
export const ROUTE_PATHS = [
  "/login",
  "/invite/:token",
  "/app",
  "/tasks",
  "/boards/archived",
  "/boards/:boardId",
  "/boards/:boardId/tasks/:taskKey",
  "/boards/:boardId/archive",
  "/boards/:boardId/settings",
  "/reports",
  "/reports/departments/:id",
  "/reports/employees/:id",
  "/reports/employees/:id/tasks/:taskKey",
  "/reports/projects/:id",
  "/reports/projects/:id/tasks/:taskKey",
  "/reports/tasks/:taskKey",
  "/reports/attention/tasks/:taskKey",
  "/notes",
  "/notes/:noteId",
  "/vacations",
  "/vacations/planner",
  "/vacations/employees",
  "/vacations/settings",
  "/admin/users",
  "/admin/users/:id",
  "/admin/roles",
  "/admin/dictionaries/projects",
  "/admin/dictionaries/priorities",
  "/admin/dictionaries/task-types",
  "/admin/dictionaries/workload",
  "/admin/dictionaries/departments",
  "/notifications",
  "/settings/profile",
  "/settings/subscriptions",
] as const;
